<template>
    <DefaultHeader />
    <div :class="{ 'd-none': $store.state.hiddenBody }" class="container-fluid">
        <!-- Sélecteur de mois -->
        <div class="row d-flex justify-content-center mb-3">
            <div class="col-2">
                <input type="month" v-model="selectedMonth" class="form-control" @change="fetchBilling" />
            </div>
            <div class="col-auto">
                <button
                    type="button"
                    class="btn btn-outline-primary"
                    :disabled="loadingPdf"
                    @click="downloadMonthlyPdfReport"
                >
                    <span v-if="loadingPdf" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Exporter en PDF
                </button>
            </div>
        </div>

        <!-- Tableau -->
        <div class="row d-flex justify-content-center">
            <div class="col-11 mb-3 mb-lg-5">
                <div class="card table-card">
                    <div class="table-responsive">
                        <table class="table">
                            <thead class="bg-body text-muted">
                                <tr>
                                    <th>Client</th>
                                    <th>Offre</th>
                                    <th>Prix unitaire</th>
                                    <th>Nombre</th>
                                    <th>Montant total</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="client in billingData" :key="client.clientId">
                                    <tr v-for="pkg in client.packages" :key="pkg.commercialPackageId">
                                        <td>{{ client.clientName }}</td>
                                        <td>{{ pkg.commercialPackageName }}</td>
                                        <td>{{ formatPrice(pkg.commercialPackagePrice) }}</td>
                                        <td>{{ pkg.count }}</td>
                                        <td>{{ formatPrice(pkg.totalAmount) }}</td>
                                        <td></td>
                                    </tr>
                                    <!-- Détail de facturation affiché sous le client sélectionné -->
                                    <tr
                                        v-if="selectedClientForDetails && selectedClientForDetails.clientId === client.clientId">
                                        <td colspan="6">
                                            <div v-if="loadingClientDetails" class="text-center py-3">
                                                <div class="spinner-border" role="status">
                                                    <span class="visually-hidden">Chargement...</span>
                                                </div>
                                            </div>
                                            <div v-else>
                                                <div v-if="clientDetails.length === 0">
                                                    <p class="mb-0">
                                                        Aucun détail de facturation pour ce client sur ce mois.
                                                    </p>
                                                </div>
                                                <div v-else class="table-responsive">
                                                    <table class="table table-sm mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>Balise</th>
                                                                <th>IMEI</th>
                                                                <th>Offre</th>
                                                                <th>Prix unitaire</th>
                                                                <th>Ratio</th>
                                                                <th>Télésurveillance</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr v-for="row in clientDetails" :key="row.id"
                                                                :class="{ 'text-decoration-line-through text-muted table-secondary': Number(row.ratio) === 0, 'table-warning': Number(row.ratio) !== 0 && Number(row.ratio) !== 1 }">
                                                                <td>{{ row.baliseName }}</td>
                                                                <td>{{ row.imei }}</td>
                                                                <td>{{ row.commercialPackageName }}</td>
                                                                <td>{{ formatPrice(row.commercialPackagePrice) }}</td>
                                                                <td>{{ row.ratio }}</td>
                                                                <td>
                                                                    <span v-if="row.remoteMonitoring">Oui</span>
                                                                    <span v-else>Non</span>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <!-- Total par client -->
                                    <tr class="table-secondary fw-bold">
                                        <td colspan="4" class="text-end">
                                            Total {{ client.clientName }}
                                        </td>
                                        <td>{{ formatPrice(client.clientTotal) }}</td>
                                        <td class="text-center">
                                            <button type="button" class="btn btn-sm btn-outline-primary"
                                                @click="loadClientDetails(client)">
                                                Voir détail
                                            </button>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                            <tfoot>
                                <tr class="table-dark fw-bold">
                                    <td colspan="4" class="text-end">Total global</td>
                                    <td>{{ formatPrice(totalGlobal) }}</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <DefaultFooter />
</template>

<script>
import DefaultHeader from "@/components/DefaultHeader.vue";
import DefaultFooter from "@/components/DefaultFooter.vue";
import BillingService from "@/services/billing.service.js";

export default {
    components: { DefaultHeader, DefaultFooter },
    data() {
        const today = new Date();
        today.setMonth(today.getMonth() - 1); // recule d'1 mois
        const prevMonth = today.toISOString().slice(0, 7); // YYYY-MM

        return {
            selectedMonth: prevMonth,
            billingData: [],
            totalGlobal: 0,
            selectedClientForDetails: null,
            clientDetails: [],
            loadingClientDetails: false,
            loadingPdf: false,
        };
    },
    mounted() {
        this.fetchBilling();
    },
    methods: {
        fetchBilling() {
            // reset du détail quand on change de mois
            this.selectedClientForDetails = null;
            this.clientDetails = [];
            this.loadingClientDetails = false;

            BillingService.getByMonth(this.selectedMonth)
                .then((response) => {
                    this.billingData = response.data.clients;
                    this.totalGlobal = response.data.totalAllClients;
                })
                .catch((err) => {
                    console.error("Erreur récupération facturation :", err);
                });
        },
        loadClientDetails(client) {
            // Si on clique sur le client déjà sélectionné -> on referme
            if (this.selectedClientForDetails && this.selectedClientForDetails.clientId === client.clientId) {
                this.selectedClientForDetails = null;
                this.clientDetails = [];
                return;
            }

            // Sinon on charge le détail
            this.selectedClientForDetails = client;
            this.loadingClientDetails = true;
            this.clientDetails = [];

            BillingService.getClientDetailsByMonth(client.clientId, this.selectedMonth)
                .then((response) => {
                    this.clientDetails = response.data || [];
                })
                .catch((err) => {
                    console.error("Erreur récupération détail facturation client :", err);
                    this.clientDetails = [];
                })
                .finally(() => {
                    this.loadingClientDetails = false;
                });
        },
        async downloadMonthlyPdfReport() {
            this.loadingPdf = true;

            try {
                const resp = await BillingService.getMonthlyPdfReport(this.selectedMonth);
                const { downloadUrl } = resp.data || {};

                if (!downloadUrl) {
                    throw new Error("downloadUrl manquant");
                }

                const apiBase = process.env.VUE_APP_API_URL;
                const origin = new URL(apiBase).origin;
                const absolute = new URL(downloadUrl, origin).toString();

                const a = document.createElement("a");
                a.href = absolute;
                a.target = "_blank";
                a.rel = "noopener";
                document.body.appendChild(a);
                a.click();
                a.remove();
            } catch (err) {
                console.error("Erreur lors de l'export PDF de facturation :", err);
            } finally {
                this.loadingPdf = false;
            }
        },
        formatPrice(value) {
            if (value == null || isNaN(value)) return "0,00 €";
            return new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(value);
        },
    },
};
</script>